import { resolveDisplayTimezone } from './userTimezone.js';

/**
 * Date helper — avoid "day jump" when sending dates to API.
 * Use toYMD / toMySQLDateTime in payloads; parseDateSafe / parseDateTimeSafe when reading from API.
 *
 * Problem: If user picks 2026-02-10 in UTC+2, Date is 2026-02-10 00:00:00 local
 * → toISOString() gives 2026-02-09T22:00:00.000Z → server date('Y-m-d', strtotime(...)) yields 2026-02-09 (one day back).
 *
 * Solution: Send calendar dates using local components and datetime instants as UTC. Never use toISOString().slice(0,10) for date-only fields.
 */

/**
 * Format Date or date string to YYYY-MM-DD (local date, no UTC shift). Use this in API payloads for date-only fields.
 * @param {Date|string|null} d
 * @returns {string|null}
 */
export function toYMD(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : parseDateSafe(d);
  if (!dt || Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Calendar date → YYYY-MM-DD for API payloads (filters, forms). Empty/falsy → ''.
 * Uses toYMD / parseDateSafe — never toISOString().slice(0, 10).
 * @param {Date|string|null|undefined} value
 * @returns {string}
 */
export function toApiDate(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date) return toYMD(value) || '';
  const parsed = parseDateSafe(value);
  if (parsed) return toYMD(parsed) || '';
  return '';
}

/**
 * Format date-only value for US display (MM/DD/YYYY).
 * Uses parseDateSafe — never `new Date('YYYY-MM-DD')` (UTC shift risk).
 * @param {Date|string|null|undefined} value
 * @returns {string} empty string for null/invalid
 */
export function formatDateUs(value) {
  const parsed = parseDateSafe(value);
  if (!parsed) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${month}/${day}/${parsed.getFullYear()}`;
}

/**
 * Parse YYYY-MM-DD (or string starting with YYYY-MM-DD) to Date without timezone conversion.
 * Use when binding API response or stored value to a date picker.
 * @param {string|Date|null} dateString
 * @returns {Date|null}
 */
export function parseDateSafe(dateString) {
  if (!dateString) return null;
  if (dateString instanceof Date) return Number.isNaN(dateString.getTime()) ? null : dateString;
  const raw = String(dateString).trim();
  if (!/^\d{4}-\d{2}-\d{2}(?:$|[T ]\d{2}:\d{2})/.test(raw)) return null;
  const datePart = raw.substring(0, 10);
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
  return date;
}

/**
 * Today's calendar date as a local midnight Date (DatePicker / date-only fields).
 * @param {Date} [now]
 * @returns {Date|null}
 */
export function localTodayDate(now = new Date()) {
  return parseDateSafe(toYMD(now instanceof Date ? now : new Date()));
}

/**
 * Format an instant as a UTC MySQL datetime, independent of browser timezone.
 * @param {Date|string|null} date
 * @returns {string|null}
 */
export function toMySQLDateTime(date) {
  if (!date) return null;
  const dt = parseUtcDateTime(date);
  if (!dt) return null;
  return dt.toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * True when API value is a calendar date only (no time-of-day).
 * @param {string|Date|null} value
 * @returns {boolean}
 */
export function isDateOnlyApiValue(value) {
  if (value == null || value === '') return false;
  if (value instanceof Date) return false;
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * True when value includes a time component (DB datetime or ISO).
 * @param {string|Date|null} value
 * @returns {boolean}
 */
export function hasDateTimeComponent(value) {
  if (value == null || value === '') return false;
  if (value instanceof Date) return true;
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:/.test(s) || /[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
}

/**
 * Parse API/DB datetime stored as UTC (naive YYYY-MM-DD HH:mm:ss or ISO with Z).
 * @param {string|Date|null} dateTimeString
 * @returns {Date|null}
 */
export function parseUtcDateTime(dateTimeString) {
  if (!dateTimeString) return null;
  if (dateTimeString instanceof Date) {
    return Number.isNaN(dateTimeString.getTime()) ? null : dateTimeString;
  }
  const s = String(dateTimeString).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?([Zz]|[+-]\d{2}:?\d{2})?$/.exec(s);
  if (!m || !parseDateSafe(`${m[1]}-${m[2]}-${m[3]}`)) return null;
  if (Number(m[4]) > 23 || Number(m[5]) > 59 || Number(m[6] || 0) > 59) return null;
  const offset = m[8] || 'Z';
  if (offset !== 'Z' && offset !== 'z') {
    const digits = offset.slice(1).replace(':', '');
    if (Number(digits.slice(0, 2)) > 23 || Number(digits.slice(2)) > 59) return null;
  }
  const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] || '00'}.${(m[7] || '').padEnd(3, '0').slice(0, 3)}${offset.toUpperCase()}`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parse MySQL datetime (YYYY-MM-DD HH:MM:SS) or ISO with T to Date in local time (no UTC conversion of the value).
 * @param {string|null} dateTimeString
 * @returns {Date|null}
 */
export function parseDateTimeSafe(dateTimeString) {
  if (!dateTimeString) return null;
  if (dateTimeString instanceof Date) return Number.isNaN(dateTimeString.getTime()) ? null : dateTimeString;
  const raw = String(dateTimeString).trim();
  if (isDateOnlyApiValue(raw)) return parseDateSafe(raw);
  const instant = parseUtcDateTime(raw);
  if (!instant) return null;
  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(raw)) return instant;
  const date = new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate(), instant.getUTCHours(), instant.getUTCMinutes(), instant.getUTCSeconds(), instant.getUTCMilliseconds());
  if (date.getDate() !== instant.getUTCDate() || date.getHours() !== instant.getUTCHours() || date.getMinutes() !== instant.getUTCMinutes()) return null;
  return date;
}

/**
 * Add calendar months to a local date (handles month length / leap years like Date#setMonth).
 * @param {Date|string|null} date
 * @param {number} months
 * @returns {Date|null}
 */
export function addCalendarMonths(date, months) {
  const d = date instanceof Date ? new Date(date.getTime()) : parseDateSafe(date);
  if (!d) return null;
  const day = d.getDate();
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setMonth(out.getMonth() + months);
  if (out.getDate() !== day) {
    out.setDate(0);
  }
  return out;
}

/**
 * Inclusive end date for a 12-month plan window: (periodFrom + 12 months) − 1 day.
 * @param {Date|string|null} periodFrom
 * @returns {Date|null}
 */
export function inclusiveEndAfterTwelveMonthsFrom(periodFrom) {
  const end = addCalendarMonths(periodFrom, 12);
  if (!end) return null;
  end.setDate(end.getDate() - 1);
  return end;
}

/**
 * Default 12-month inclusive plan window: anchor day (default today local) through end = anchor + 12 months − 1 day.
 * @param {Date} [anchorDate] defaults to now (local calendar day)
 * @returns {{ from: Date, to: Date }}
 */
export function defaultTwelveMonthPlanPeriodFromDate(anchorDate = new Date()) {
  const base = anchorDate instanceof Date ? anchorDate : parseDateSafe(anchorDate);
  const ref = base && !Number.isNaN(base.getTime()) ? base : new Date();
  const from = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const to = inclusiveEndAfterTwelveMonthsFrom(from);
  if (!to) {
    return { from, to: from };
  }
  return { from, to };
}

/**
 * Resolve employee list "created" period preset to API date bounds (YYYY-MM-DD, local calendar).
 * @param {string|null|undefined} period
 * @param {string|Date|null|undefined} customFrom
 * @param {string|Date|null|undefined} customTo
 * @returns {{ from: string, to: string }}
 */
export function resolveCreatedPeriodRange(period, customFrom = '', customTo = '') {
  if (!period) return { from: '', to: '' };
  if (period === 'custom') {
    return { from: toApiDate(customFrom), to: toApiDate(customTo) };
  }

  const today = displayTodayDate();
  if (!today) return { from: '', to: '' };

  const to = toYMD(today) || '';
  const shiftDays = (days) => {
    const next = new Date(today);
    next.setDate(next.getDate() + days);
    return toYMD(next) || '';
  };

  switch (period) {
    case 'today':
      return { from: to, to };
    case 'last_7_days':
      return { from: shiftDays(-6), to };
    case 'this_month':
      return { from: toYMD(new Date(today.getFullYear(), today.getMonth(), 1)) || '', to };
    case 'last_30_days':
      return { from: shiftDays(-29), to };
    case 'last_90_days':
      return { from: shiftDays(-89), to };
    default:
      return { from: '', to: '' };
  }
}

/**
 * Split a trailing AM/PM marker. "2 PM" and "9:15am" are US clock; "17:00" stays 24-hour.
 * @param {string} raw
 * @returns {{ clock: string, meridian: ''|'am'|'pm' }}
 */
function splitClockMeridian(raw) {
  const match = /^(.*?)[\s]*([ap])\.?m\.?$/i.exec(raw);
  if (!match) return { clock: raw, meridian: '' };
  const clock = match[1].trim();
  if (!clock) return { clock: raw, meridian: '' };
  return { clock, meridian: match[2].toLowerCase() === 'p' ? 'pm' : 'am' };
}

/**
 * Normalize a clock string to 24-hour HH:MM.
 * Minutes are optional ("12" → "12:00"). AM/PM is optional ("2 PM" → "14:00", "12 AM" → "00:00").
 * Without AM/PM the hour is 0–23, so "9" is 09:00 and "17" is 17:00.
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function normalizeClockTimeHm(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }
  if (value == null || value === '') return '';
  const split = splitClockMeridian(String(value).trim());
  let raw = split.clock.replace(/\s+/g, '');
  if (!raw) return '';
  if (!raw.includes(':')) {
    raw = raw.replace(/[.,]/, ':');
  } else {
    raw = raw.replace(/\.\d+$/, '');
  }
  raw = raw.replace(/:+$/, '');
  if (/^\d{3,4}$/.test(raw)) {
    const padded = raw.padStart(4, '0');
    raw = `${padded.slice(0, 2)}:${padded.slice(2)}`;
  }
  const match = /^(\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?$/.exec(raw);
  if (!match) return '';
  let hour = Number(match[1]);
  const minute = match[2] != null && match[2] !== '' ? Number(match[2]) : 0;
  const second = match[3] != null && match[3] !== '' ? Number(match[3]) : 0;
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    !Number.isInteger(second) ||
    minute > 59 ||
    second > 59
  ) {
    return '';
  }
  if (split.meridian) {
    if (hour < 1 || hour > 12) return '';
    if (hour === 12) hour = split.meridian === 'am' ? 0 : 12;
    else if (split.meridian === 'pm') hour += 12;
  } else if (hour > 23) {
    return '';
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Parse HH:mm (or HH:mm:ss) to a local Date on a fixed calendar day (time-only pickers).
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseTimeHm(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const hm = normalizeClockTimeHm(value);
  if (!hm) return null;
  const [hours, minutes] = hm.split(':').map(Number);
  return new Date(1970, 0, 1, hours, minutes, 0, 0);
}

/**
 * Format Date to HH:mm for API payloads and display.
 * @param {Date|null|undefined} date
 * @returns {string}
 */
export function formatTimeHm(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * UTC/API instant → epoch ms for sorting/comparison. Never use bare `new Date(apiString)`.
 * @param {string|Date|null|undefined} value
 * @returns {number}
 */
export function instantToMs(value) {
  if (value == null || value === '') return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }
  const parsed = parseUtcDateTime(value);
  if (!parsed) return 0;
  const ms = parsed.getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/** @type {ReadonlyArray<{ label: string, value: string }>} */
export const CREATED_PERIOD_FILTER_OPTIONS = Object.freeze([
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'This month', value: 'this_month' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 90 days', value: 'last_90_days' },
  { label: 'Custom range', value: 'custom' },
]);

/** Today's calendar components in the configured display zone, for date-only controls. */
export function displayTodayDate(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: resolveDisplayTimezone(), year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return parseDateSafe(`${values.year}-${values.month}-${values.day}`);
}

/** Presentation only: stored HH:mm clocks remain unchanged. */
export function formatClockTimeUs(value) {
  const hm = normalizeClockTimeHm(value);
  if (!hm) return '';
  const [hour, minute] = hm.split(':').map(Number);
  return `${String(hour % 12 || 12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}
